import {
  DataGrid,
  GridActionsCell,
  type GridColDef,
  type GridColumnVisibilityModel,
  GridActionsCellItem,
  type GridRowModesModel,
  type GridRowId,
  GridRowModes,
  type GridEventListener,
  GridRowEditStopReasons,
} from '@mui/x-data-grid'
import { Box, TextField, Switch, Tabs, Tab } from '@mui/material'
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material'
import SearchField from 'components/search-field'
import Reseller from 'components/admin/reseller'
import { t } from 'i18next'
import { useContext, useEffect, useRef, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchPartners, editPartner } from 'api/partner'
import type { AppState } from 'state/index'
import { get } from 'es-toolkit/compat'
import { unitConvert } from '@farmtrx/units'
import { trackEvent } from 'api/usage-tracking'
import { TEST_IDS } from 'test-ids'
import { FEATURE_FLAGS } from 'feature-flags'
import FeatureFlagsContext from 'context/feature-flags-context'
import colors from 'common/colors'

type PartnerData = {
  id: string
  name: string
  email: string
  organizationCount: number
  subscriptionMsrp: number
  discountRate: number
  accountEnabled: boolean
  paid: number
  freeYear: number
  notPaid: number
  comped: number
  autoRenew: boolean
  harvestedAreaSqm?: number
}

export default function Partner(props) {
  const dispatch = useDispatch()
  const permissions = useSelector(
    (state: AppState) => state.account.permissions,
  )
  const [activeTab, setActiveTab] = useState(0)
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  })

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({})
  const [rowCount, setRowCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [searchValue, setSearchValue] = useState<string>('')
  const [rowSelected, setSelectedRow] = useState<string | number>(-1)
  const [debouncedSearch, setDebouncedSearch] = useState<string>('')
  const debounceTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  )

  const [partnerList, setPartnerList] = useState<PartnerData[]>([])

  const [columns, setColumns] = useState<GridColDef[]>([])
  const isAdmin = permissions.includes('farmtrx:admin')
  const [touchedFields, setTouchedFields] = useState<
    Record<string, Set<string>>
  >({})
  const features = useContext(FeatureFlagsContext)
  const orders = get(features, FEATURE_FLAGS.orders, { isEnabled: false })
  const userPrefs = useSelector((state) =>
    get(state as AppState, 'account.singleton.prefs'),
  )

  function fetchData(page, pageSize, search, ignore) {
    fetchPartners(page, pageSize, search)
      .then((res) => {
        if (!ignore) {
          const { results, pagination } = res.data
          const _partnerList = (results || []).map((partner) => ({
            id: partner.id,
            name: partner.name,
            email: partner.email,
            organizationCount: partner.organizationCount || 0,
            subscriptionMsrp: partner.subscriptionMsrp || 0,
            discountRate: partner.discountRate || 0,
            accountEnabled: partner.accountEnabled,
            paid: partner.paid || 0,
            freeYear: partner.freeYear || 0,
            notPaid: partner.notPaid || 0,
            comped: partner.comped || 0,
            autoRenew: partner.autoRenew,
            harvestedAreaSqm: partner.harvestedAreaSqm || 0,
          }))
          setPartnerList(_partnerList)
          setRowCount(pagination?.total || 0)
        }
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  useEffect(() => {
    let ignore = false
    setLoading(true)
    fetchData(
      (paginationModel.page || 0) + 1,
      paginationModel.pageSize,
      debouncedSearch,
      ignore,
    )
    return () => {
      ignore = true
    }
  }, [paginationModel, debouncedSearch])

  const onSearch = (value: string) => {
    setSearchValue(value)
  }

  const handleEdit = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.Edit },
      }))
    },
    [setRowModesModel],
  )

  const handleSave = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View },
      }))
    },
    [setRowModesModel],
  )

  const saveData = useCallback(
    async (dataToSave: any) => {
      const obj = {
        discountRate: Number(dataToSave.discountRate),
        autoRenew: dataToSave.autoRenew,
      }
      await editPartner(dataToSave.id, obj)
      trackEvent('partner_edited', {
        discount: dataToSave.discountRate,
        autoRenew: dataToSave.autoRenew,
      })
      setSelectedRow(-1)
      return dataToSave
    },
    [rowSelected],
  )

  const processRowUpdate = async (newRow: PartnerData, oldRow: PartnerData) => {
    const result = await saveData(newRow)
    setPartnerList((prevRows) =>
      prevRows.map((row) => (row.id === result.id ? result : row)),
    )
    return result
  }

  const handleCancel = useCallback(
    (id: GridRowId) => {
      setRowModesModel((prev) => ({
        ...prev,
        [id]: { mode: GridRowModes.View, ignoreModifications: true },
      }))
      const originalRow = partnerList.find((row) => row.id === id)
      if (originalRow) {
        setPartnerList((prevRows) =>
          prevRows.map((row) => (row.id === id ? originalRow : row)),
        )
      }
    },
    [setRowModesModel, partnerList],
  )

  useEffect(() => {
    if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearch(searchValue)
    }, 300)
    return () => {
      if (debounceTimeout.current) clearTimeout(debounceTimeout.current)
    }
  }, [searchValue])

  const markTouched = useCallback((rowId: string, field: string) => {
    setTouchedFields((prev) => {
      const existing = prev[rowId] ? new Set(prev[rowId]) : new Set<string>()
      existing.add(field)
      return { ...prev, [rowId]: existing }
    })
  }, [])
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true
    }
  }

  const [columnVisibility, setColumnVisibility] =
    useState<GridColumnVisibilityModel>({
      tenantId: false,
      organizationName: true,
      numberOfOrganizations: true,
      paid: true,
      freeYear: true,
      notPaid: true,
      comped: true,
      discountRate: true,
    })

  useEffect(() => {
    const _columns: GridColDef[] = [
      {
        field: 'name',
        headerName: t('partner_name'),
        minWidth: 150,
        maxWidth: 300,
        flex: 1,
      },
      {
        field: 'organizationCount',
        headerName: t('organization_count'),
        minWidth: 200,
        maxWidth: 400,
        align: 'left',
        flex: 1,
      },
      {
        field: 'harvestedAreaSqm',
        headerName: t('harvested_area'),
        minWidth: 200,
        maxWidth: 400,
        align: 'left',
        flex: 1,
        renderCell: (params) => {
          const area = params.row.harvestedAreaSqm
            ? `${unitConvert
                .convertAreaToPref(
                  params.row.harvestedAreaSqm,
                  userPrefs['units']['area_in'],
                )
                .toFixed(2)} ${userPrefs['units']['area_in']}`
            : `0 ${userPrefs?.['units']['area_in']}`
          return <span>{area}</span>
        },
      },
      {
        field: 'paid',
        headerName: t('paid'),
        minWidth: 100,
        maxWidth: 200,
        align: 'left',
        flex: 1,
      },
      {
        field: 'freeYear',
        headerName: t('free_year'),
        minWidth: 100,
        maxWidth: 200,
        align: 'left',
        flex: 1,
      },
      {
        field: 'notPaid',
        headerName: t('not_paid'),
        minWidth: 100,
        maxWidth: 200,
        align: 'left',
        flex: 1,
      },
      {
        field: 'comped',
        headerName: t('comped'),
        minWidth: 100,
        maxWidth: 200,
        align: 'left',
        flex: 1,
      },
      {
        field: 'discountRate',
        headerName: t('discount_rate'),
        minWidth: 150,
        maxWidth: 200,
        align: 'left',
        flex: 1,
        editable: true,
        renderCell: (params) => `${params.row.discountRate}%`,
        preProcessEditCellProps: (params) => {
          let error = false
          const parsedValue = Number(params.props.value)
          if (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100) {
            error = true
          }

          return { ...params.props, error }
        },
        renderEditCell: (params) => {
          const parsedValue = Number(params.row.discountRate)
          const isTouched =
            !!touchedFields[params.id as string]?.has('discountRate')
          const hasError =
            isTouched &&
            (isNaN(parsedValue) || parsedValue < 0 || parsedValue > 100)
          return (
            <TextField
              value={params.row.discountRate ?? ''}
              onChange={(e) => {
                const value = e.target.value
                setPartnerList((prevRows: PartnerData[]) =>
                  prevRows.map((row) =>
                    row.id === params.id
                      ? { ...row, discountRate: Number(value) }
                      : row,
                  ),
                )
                params.api.setEditCellValue({
                  id: params.id,
                  field: 'discountRate',
                  value,
                })
              }}
              onBlur={() => markTouched(params.id as string, 'discountRate')}
              error={hasError}
              label={t('enter_discount_rate')}
              size="small"
              fullWidth
              autoFocus={params.hasFocus}
              variant={'filled'}
            />
          )
        },
      },
      {
        field: 'autoRenew',
        headerName: t('auto_renew'),
        minWidth: 150,
        maxWidth: 200,
        align: 'left',
        flex: 1,
        editable: true,
        preProcessEditCellProps: (params) => {
          return { ...params.props, error: false }
        },
        renderCell: (params) => (
          <Switch checked={params.row.autoRenew} disabled />
        ),
        renderEditCell: (params) => (
          <Switch
            checked={params.row.autoRenew}
            onChange={(e) => {
              const value = e.target.checked
              setPartnerList((prevRows: PartnerData[]) =>
                prevRows.map((row) =>
                  row.id === params.id ? { ...row, autoRenew: value } : row,
                ),
              )
              params.api.setEditCellValue({
                id: params.id,
                field: 'autoRenew',
                value,
              })
            }}
          />
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        editable: true,
        preProcessEditCellProps: (params) => {
          return { ...params.props, error: false }
        },
        renderCell: (params) => {
          return (
            <GridActionsCell {...params}>
              <GridActionsCellItem
                icon={<EditIcon />}
                label={t('edit')}
                onClick={() => handleEdit(params.id)}
              />
            </GridActionsCell>
          )
        },
        renderEditCell: (params) => {
          const { error, isProcessingProps, ...rest } = params
          return (
            <GridActionsCell {...rest}>
              <GridActionsCellItem
                icon={<SaveIcon />}
                label={t('save')}
                onClick={() => handleSave(rest.id)}
              />
              <GridActionsCellItem
                icon={<CancelIcon />}
                label={t('cancel')}
                onClick={() => handleCancel(rest.id)}
              />
            </GridActionsCell>
          )
        },
      },
    ]
    setColumns(_columns)
  }, [permissions])

  function CustomNoRowsOverlayOrganizations() {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        {t('no_partners')}
      </div>
    )
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  return (
    <Box
      sx={{
        width: 1,
        display: 'flex',
        flexDirection: 'column',
        padding: '1rem',
        height: '100%',
      }}
    >
      <h1>{orders.isEnabled ? t('partners_resellers') : t('partners')}</h1>
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab
          data-testid={TEST_IDS.adminPartnersTab}
          label={t('partners')}
          id="partners-tab"
          aria-controls="partners-tabpanel"
        />
        {orders.isEnabled && (
          <Tab
            data-testid={TEST_IDS.adminResellersTab}
            label={t('resellers')}
            id="resellers-tab"
            aria-controls="resellers-tabpanel"
          />
        )}
      </Tabs>
      {activeTab === 0 && (
        <>
          <SearchField
            className="partner-search"
            sx={{ width: '100%' }}
            name={searchValue}
            placeholder={t('search_by_org_name_email')}
            onSearch={onSearch}
          />
          <div
            style={{
              flexGrow: 1,
              width: '100%',
              marginTop: '2rem',
            }}
          >
            <Box
              sx={{
                my: 1,
                display: 'flex',
                flexDirection: 'column',
                padding: '1rem',
                overflowX: 'auto',
                minHeight: `calc(52px * ${paginationModel?.pageSize || 10} + 56px)`,
                // 52px is approx row height, 56px for header/pagination controls
                '& .data-grid--header': {
                  background: colors.greyBackground,
                  textTransform: 'uppercase',
                },
              }}
            >
              <DataGrid
                pageSizeOptions={[10, 25, 50]}
                columnVisibilityModel={columnVisibility}
                onColumnVisibilityModelChange={(model) =>
                  setColumnVisibility(model)
                }
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                rowModesModel={rowModesModel}
                onRowModesModelChange={setRowModesModel}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 10, page: 0 },
                  },
                  sorting: {
                    sortModel: [{ field: 'name', sort: 'asc' }],
                  },
                }}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                rowCount={rowCount}
                rows={partnerList}
                loading={loading}
                slots={{
                  noRowsOverlay: CustomNoRowsOverlayOrganizations,
                }}
                columns={columns}
                sx={{
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: colors.greyBackground,
                    textTransform: 'uppercase',
                  },
                }}
              />
            </Box>
          </div>
        </>
      )}
      {activeTab === 1 && <Reseller />}
    </Box>
  )
}
